import {
	type BaseNAICSCode,
	type BondRequest,
	formatToDollar,
	formatToPercent,
	type NAICSCodeGroupMap,
	pluralize,
} from "@inrev/common";
import { Option } from "@inrev/core";
import { useNaicsCodeGroupMap } from "@portal/api/naics-code";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useState } from "react";
import {
	finalBondFormTypeToString,
	formatEmpty,
	formatProjectLocation,
} from "../../../utils/string";
import { DefinitionList } from "../../patterns/definition-list";
import { ClickableText, Text } from "../../primitives";

type ExtractionField<T> = {
	value?: Option<T>;
	explanation?: Option<string>;
};

type SummaryField = {
	label: string;
	value: string;
	explanation: Option<string>;
};

const DOCUMENT_TYPE_LABELS: Record<BondRequest.BondRequestDocumentType, string> = {
	contract: "Contract",
	rfp: "RFP",
	bond_request_form: "Bond Request Form",
	submission_narrative: "Submission Narrative",
	email_thread: "Email Thread",
	other: "Other",
};

const SCOPE_LABELS: Record<BondRequest.BondRequestExtractionScope, string> = {
	full: "Full extraction",
	minimal: "Minimal extraction",
};

type FullOutput = BondRequest.BondRequestExtractionOutputFull;

type BoolExtractionKey = {
	[K in keyof FullOutput]: boolean extends NonNullable<FullOutput[K]["value"]> ? K : never;
}[keyof FullOutput];

type BoolFieldConfig = {
	key: BoolExtractionKey;
	label: string;
};

// Mirrors the contractDamage / contractHazard domain enums so the panel groups these the same way
// underwriting does.
const DAMAGE_FIELDS: BoolFieldConfig[] = [
	{ key: "consequentialDamages", label: "Consequential damages" },
	{ key: "primeContractDamages", label: "Prime contract damages" },
	{ key: "liquidatedOver5k", label: "Liquidated damages over $5,000" },
	{ key: "otherDamages", label: "Loss of use" },
];

const HAZARD_FIELDS: BoolFieldConfig[] = [
	{ key: "designBuild", label: "Design-build" },
	{ key: "environmentalRemediation", label: "Environmental or remediation work" },
	{ key: "superfundSite", label: "Superfund site" },
	{ key: "efficiencyGuarantees", label: "Efficiency guarantees" },
	{ key: "sovereignNation", label: "Sovereign nation owner" },
	{ key: "renewableContract", label: "Renewable contract" },
	{ key: "takeover", label: "Takeover" },
];

function formatScopeOfWork(codes: readonly BaseNAICSCode[], groupMap: NAICSCodeGroupMap): string {
	return codes.map((code) => groupMap.get(code)?.label ?? code).join(", ");
}

function formatBondAmountType(type: BondRequest.BondAmountType): string {
	return type === "percent" ? "Percent of bid" : "Fixed dollars";
}

const EMPTY_DISPLAY = formatEmpty(null);

function presentText(value: Option<string>): Option<string> {
	return Option.flatMap(value, (text) => {
		const trimmed = text.trim();
		return trimmed === "" ? undefined : trimmed;
	});
}

function formatExtractedDollars(amount: number): string {
	return Option.getOrElse(formatToDollar(amount), String(amount));
}

function formattedField<T>(
	label: string,
	field: ExtractionField<T> | undefined,
	formatValue: (value: T) => string,
): Option<SummaryField> {
	if (field === undefined) return undefined;
	return {
		label,
		value: Option.mapOrElse(field.value, formatValue, EMPTY_DISPLAY),
		explanation: field.explanation,
	};
}

function stringField(
	label: string,
	field: ExtractionField<string> | undefined,
): Option<SummaryField> {
	return formattedField(label, field, (value) =>
		Option.getOrElse(presentText(value), EMPTY_DISPLAY),
	);
}

function numberField(
	label: string,
	field: ExtractionField<number> | undefined,
	format: (n: number) => string,
): Option<SummaryField> {
	return formattedField(label, field, format);
}

function boolField(
	label: string,
	field: ExtractionField<boolean> | undefined,
): Option<SummaryField> {
	return formattedField(label, field, (value) => (value ? "Yes" : "No"));
}

// Extracted dates arrive as ISO strings; anything the model returns in another shape passes through.
function dateField(
	label: string,
	field: ExtractionField<string> | undefined,
): Option<SummaryField> {
	return formattedField(label, field, (value) =>
		Option.mapOrElse(
			presentText(value),
			(trimmed) => {
				const parsed = DateTime.fromISO(trimmed);
				return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : trimmed;
			},
			EMPTY_DISPLAY,
		),
	);
}

function locationField(output: BondRequest.BondRequestExtractionOutput): Option<SummaryField> {
	if (output.projectCity === undefined && output.projectState === undefined) return undefined;
	return {
		label: "Location",
		value: formatProjectLocation(output.projectCity?.value, output.projectState?.value),
		explanation: Option.first([output.projectCity?.explanation, output.projectState?.explanation]),
	};
}

function scopeOfWorkField(
	output: BondRequest.BondRequestExtractionOutput,
	groupMap: NAICSCodeGroupMap,
): Option<SummaryField> {
	if (output.scopeOfWork === undefined) return undefined;
	return {
		label: "Scope of work",
		value: Option.mapOrElse(
			output.scopeOfWork.value,
			(codes) => (codes.length > 0 ? formatScopeOfWork(codes, groupMap) : EMPTY_DISPLAY),
			EMPTY_DISPLAY,
		),
		explanation: output.scopeOfWork.explanation,
	};
}

function buildProjectFields(
	output: BondRequest.BondRequestExtractionOutput,
	groupMap: NAICSCodeGroupMap,
): SummaryField[] {
	return Option.filterNone([
		stringField("Project name", output.projectName),
		stringField("Project number", output.projectNumber),
		stringField("Project description", output.projectDescription),
		locationField(output),
		scopeOfWorkField(output, groupMap),
	]);
}

function buildAmountsAndDatesFields(
	output: BondRequest.BondRequestExtractionOutput,
): SummaryField[] {
	return Option.filterNone([
		numberField("Contract / bid amount", output.contractOrBidAmount, formatExtractedDollars),
		numberField("Bond amount", output.bondAmount, formatExtractedDollars),
		formattedField("Bond amount type", output.bondAmountType, formatBondAmountType),
		numberField("Bond amount % of bid", output.bondAmountAsPercentOfBid, (n) => formatToPercent(n)),
		dateField("Bid date", output.bidDate),
		dateField("Contract date", output.contractDate),
	]);
}

function buildTermsFields(output: BondRequest.BondRequestExtractionOutput): SummaryField[] {
	return Option.filterNone([
		formattedField("Final bond form type", output.finalBondFormType, (value) =>
			Option.getOrElse(finalBondFormTypeToString(value), String(value)),
		),
		numberField("Time to complete", output.timeToCompleteMonths, (n) =>
			pluralize(n, "month", "months"),
		),
		numberField("Warranty period", output.warrantyPeriodMonths, (n) =>
			pluralize(n, "month", "months"),
		),
		boolField("Completion bond", output.completionBond),
		boolField("Treasury listing required", output.tListingRequired),
	]);
}

function buildBoolFields(
	output: BondRequest.BondRequestExtractionOutput,
	configs: BoolFieldConfig[],
): SummaryField[] {
	return Option.filterNone(configs.map(({ key, label }) => boolField(label, output[key])));
}

export function formatBondRequestDocumentType(
	documentType: Option<BondRequest.BondRequestDocumentType>,
): Option<string> {
	return Option.map(documentType, (type) => DOCUMENT_TYPE_LABELS[type]);
}

export function formatBondRequestExtractionScope(
	scope: Option<BondRequest.BondRequestExtractionScope>,
): Option<string> {
	return Option.map(scope, (value) => SCOPE_LABELS[value]);
}

const EMPTY_FALLBACK = (
	<Text type="body" color="secondary">
		No extracted fields were available for this file.
	</Text>
);

type BondRequestExtractionSummaryContentProps = {
	output: BondRequest.BondRequestExtractionOutput;
	emptyFallback?: ReactNode;
};

export const BondRequestExtractionSummaryContent: FC<BondRequestExtractionSummaryContentProps> = ({
	output,
	emptyFallback = EMPTY_FALLBACK,
}) => {
	const [isReasoningVisible, setIsReasoningVisible] = useState(false);
	const groupMap = useNaicsCodeGroupMap();
	const project = buildProjectFields(output, groupMap);
	const amounts = buildAmountsAndDatesFields(output);
	const terms = buildTermsFields(output);
	const damages = buildBoolFields(output, DAMAGE_FIELDS);
	const hazards = buildBoolFields(output, HAZARD_FIELDS);
	const sections = [project, amounts, terms, damages, hazards];
	const hasAny = sections.some((fields) => fields.length > 0);
	const hasReasoning = sections.some((fields) =>
		fields.some((field) => Option.isSome(presentText(field.explanation))),
	);

	if (!hasAny) return <>{emptyFallback}</>;

	const renderFields = (fields: SummaryField[], layout?: "inline") =>
		fields.map((field) => (
			<DefinitionList.Item
				key={field.label}
				label={field.label}
				caption={field.explanation}
				layout={layout}
				testId="extraction-summary-field"
			>
				{field.value}
			</DefinitionList.Item>
		));

	return (
		<div className="flex flex-col">
			{hasReasoning && (
				<div className="sticky top-0 z-20 flex w-full justify-end bg-surface-base pb-2">
					<ClickableText
						type="caption"
						color="secondary"
						onClick={() => setIsReasoningVisible((visible) => !visible)}
						testId="extraction-summary-toggle-reasoning"
						trackingId="bond-request-extraction-summary-toggle-reasoning"
					>
						{isReasoningVisible ? "Hide reasoning" : "Show reasoning"}
					</ClickableText>
				</div>
			)}
			<DefinitionList
				captionMode={isReasoningVisible ? "visible" : "collapsible"}
				stickyHeaders
				stickyHeaderClassName={hasReasoning ? "top-8" : "top-0"}
				testId="bond-request-extraction-summary-content"
			>
				{project.length > 0 && (
					<DefinitionList.Section title="Project" testId="extraction-summary-project">
						{renderFields(project)}
					</DefinitionList.Section>
				)}
				{amounts.length > 0 && (
					<DefinitionList.Section title="Amounts and dates" testId="extraction-summary-amounts">
						{renderFields(amounts)}
					</DefinitionList.Section>
				)}
				{terms.length > 0 && (
					<DefinitionList.Section title="Terms" testId="extraction-summary-terms">
						{renderFields(terms)}
					</DefinitionList.Section>
				)}
				{damages.length > 0 && (
					<DefinitionList.Section title="Damages" testId="extraction-summary-damages">
						{renderFields(damages, "inline")}
					</DefinitionList.Section>
				)}
				{hazards.length > 0 && (
					<DefinitionList.Section title="Hazards" testId="extraction-summary-hazards">
						{renderFields(hazards, "inline")}
					</DefinitionList.Section>
				)}
			</DefinitionList>
		</div>
	);
};
